// BaseMapDataOverlaysModule.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
// ───────────────────────────────────────────────────────────────────────────
//  mapDataOverlays Vuex module  –  WITH FULL PERSISTENCE
// ───────────────────────────────────────────────────────────────────────────

import mapboxgl from 'mapbox-gl';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import emitter              from '../../../../utils/bus';
import { i18n }             from '../../../../i18n';
import graph_client         from '../../../../utils/graphql/graph_client';
import { GetTrail }         from '../../../../utils/graphql/trail-graph';
import { GetPtv2PlannedTrail } from '../../../../utils/graphql/planned-trail-graph';
import { GetRecordedTrailByRecordingId } from '../../../../utils/graphql/recorded-trail-graph';
import {
  addOfficialHikes, addSlope, addWeather, addCell,
  removeCell, removeOfficialHikes, removeSlope, removeWeather,
  addCyclingRoutes, removeCyclingRoutes,
  addHeatmap, removeHeatmap,
  darkenColor, safeJsonParse, restyleOfficialHikes,
  HIIKER_TRAILS_MAIN_ID, HIIKER_TRAILS_SOURCE_ID, HIIKER_TRAILS_BORDER_ID,
  CYCLING_NCN_ID, CYCLING_LINK_ID
} from '../../helpers/BaseMapDialogHelpers'
import { addLandAccess, removeLandAccess } from '../../helpers/LandAccessOverlay'
import { LineSourceHelper } from '../../helpers/LineSourceHelper'
import {isMapReady} from "../../../../utils/mapReadyHelper";
import { RevenueCatWrapper } from "../../../../utils/RevenueCatWrapper";
import {GetHike, GetHikeForTrailCard, GetHikeByLegacy} from "../../../../utils/graphql/hikes-graph";

type RootState = any;


// ──────────────────────────── State ─────────────────────────────
export interface BaseMapDataOverlaysState {
  /* original */
  activeSources: string[];
  trailOptions : Record<string,string>;
  sourceColors : Record<string,string>;
  miniLoader   : boolean;

  /* overlay flags - now persistent */
  officialHikesActive : boolean;
  cellCoverageActive : boolean;
  slopeAngleActive   : boolean;
  landAccessActive   : boolean;
  activeWeatherLayer : { name:string|null; type:string|null };
  cyclingRoutesActive: boolean;
  heatmapActive      : boolean;
}

const state: BaseMapDataOverlaysState = {
  // Load persisted state or use defaults
  activeSources: safeJsonParse('activeTrailSources', []),

  trailOptions : {
    get get_user_recordings()      { return i18n.global.t('shared.recordings'); },
    get get_user_planned_trails()  { return i18n.global.t('base_map_dialog.base_map_data_overlays_module.my_hikes'); },
    get get_user_completed_hikes() { return i18n.global.t('shared.completed'); },
  },

  // Fallback colours only — the customisable line style in
  // `overlayLineStyles.ts` is what actually paints these layers. Kept in sync
  // with its defaults (recordings was #9E00FF here, which clashed with the
  // recording *route* colour and disagreed with iOS/Android's #C37098).
  sourceColors : {
    get_user_recordings      : '#C37098',
    get_user_planned_trails  : '#F57700',
    get_user_completed_hikes : '#005BE4',
  },

  miniLoader          : false,

  // Load persisted overlay flags or use defaults
  officialHikesActive  : safeJsonParse('officialHikesActive', false),
  cellCoverageActive  : safeJsonParse('cellCoverageActive', false),
  slopeAngleActive    : safeJsonParse('slopeAngleActive', false),
  landAccessActive    : safeJsonParse('landAccessActive', false),
  activeWeatherLayer  : safeJsonParse('activeWeatherLayer', { name:null, type:null }),
  cyclingRoutesActive : safeJsonParse('cyclingRoutesActive', false),
  heatmapActive       : safeJsonParse('heatmapActive', false),
};

let mapClickHandlerRef: ((e: any) => void) | null = null;

// ─────────────────────────── Mutations ──────────────────────────
const mutations: MutationTree<BaseMapDataOverlaysState> = {
  /* original - persist trail sources */
  SET_ACTIVE_SOURCES (s, srcs:string[]){
    s.activeSources = srcs;
    localStorage.setItem('activeTrailSources', JSON.stringify(srcs));
  },
  ADD_ACTIVE_SOURCE (s, src:string) {
    if(!s.activeSources.includes(src)){
      s.activeSources.push(src);
      localStorage.setItem('activeTrailSources', JSON.stringify(s.activeSources));
    }
  },
  REMOVE_ACTIVE_SOURCE(s,src:string) {
    s.activeSources = s.activeSources.filter(x => x !== src);
    localStorage.setItem('activeTrailSources', JSON.stringify(s.activeSources));
  },
  SET_MINI_LOADER (s,v:boolean) { s.miniLoader=v; },

  /* NEW: Persist other overlay flags */
  SET_OFFICIAL_HIKES_ACTIVE (s,v:boolean){
    s.officialHikesActive = v;
    localStorage.setItem('officialHikesActive', JSON.stringify(v));
  },
  SET_CELL_COVERAGE_ACTIVE(s,v:boolean){
    s.cellCoverageActive = v;
    localStorage.setItem('cellCoverageActive', JSON.stringify(v));
  },
  SET_SLOPE_ANGLE_ACTIVE  (s,v:boolean){
    s.slopeAngleActive = v;
    localStorage.setItem('slopeAngleActive', JSON.stringify(v));
  },
  SET_LAND_ACCESS_ACTIVE(s,v:boolean){
    s.landAccessActive = v;
    localStorage.setItem('landAccessActive', JSON.stringify(v));
  },
  SET_ACTIVE_WEATHER_LAYER(s,p:{name:string|null;type:string|null}){
    s.activeWeatherLayer = p;
    localStorage.setItem('activeWeatherLayer', JSON.stringify(p));
  },
  SET_CYCLING_ROUTES_ACTIVE(s, v: boolean) {
    s.cyclingRoutesActive = v;
    localStorage.setItem('cyclingRoutesActive', JSON.stringify(v));
  },
  SET_HEATMAP_ACTIVE(s, v: boolean) {
    s.heatmapActive = v;
    localStorage.setItem('heatmapActive', JSON.stringify(v));
  },
};

// ─────────────────────────── Actions ────────────────────────────
const actions: ActionTree<BaseMapDataOverlaysState,RootState> = {

  // ── overlay toggles (called by component) - Commit mutations which now handle persistence ──
  toggleSlopeOverlay   ({commit,state}){const nxt=!state.slopeAngleActive; commit('SET_SLOPE_ANGLE_ACTIVE',nxt); nxt?addSlope():removeSlope();},
  toggleOfficialHikesOverlay({commit,state}){const nxt=!state.officialHikesActive; commit('SET_OFFICIAL_HIKES_ACTIVE',nxt); nxt?addOfficialHikes():removeOfficialHikes();},
  toggleCellCoverageOverlay({commit,state}){const nxt=!state.cellCoverageActive; commit('SET_CELL_COVERAGE_ACTIVE',nxt); nxt?addCell():removeCell();},
  toggleLandAccessOverlay({commit,state}){const nxt=!state.landAccessActive; commit('SET_LAND_ACCESS_ACTIVE',nxt); nxt?addLandAccess():removeLandAccess();},
  toggleCyclingRoutesOverlay({commit,state}){const nxt=!state.cyclingRoutesActive; commit('SET_CYCLING_ROUTES_ACTIVE',nxt); if(nxt){addCyclingRoutes();}else{(window as any)._cyclingPopup?.remove();(window as any)._cyclingPopup=null;removeCyclingRoutes();}},
  async toggleHeatmapOverlay ({commit,state}){
    const nxt=!state.heatmapActive;
    commit('SET_HEATMAP_ACTIVE',nxt);
    if (nxt) {
      // Pro unlocks native z15 detail; free caps at z12 (see addHeatmap).
      const isPro = await RevenueCatWrapper.isProOrProPlus().catch(() => false);
      // Guard against a fast ON→OFF: the user may have toggled back off while
      // the tier check was in flight. Without this re-check we'd add orphaned
      // layers + a zoom listener the store already believes are gone.
      if (!state.heatmapActive) return;
      window.mixpanel?.track('Heatmap Overlay Toggled', {
        enabled: true, pro: isPro,
        zoom: window.map ? Math.round(window.map.getZoom() * 10) / 10 : null,
      });
      addHeatmap(isPro);
    } else {
      window.mixpanel?.track('Heatmap Overlay Toggled', {
        enabled: false,
        zoom: window.map ? Math.round(window.map.getZoom() * 10) / 10 : null,
      });
      removeHeatmap();
    }
  },
  toggleWeatherOverlay ({commit,state},p:{type:string,name:string}){
    const isCurrentlyActive = state.activeWeatherLayer.name === p.name;
    const newVal = isCurrentlyActive ? {name:null,type:null} : p;
    const oldName = state.activeWeatherLayer.name; // Get name before mutation

    commit('SET_ACTIVE_WEATHER_LAYER', newVal); // This now persists

    // Remove old layer if it existed and is different from the new one (or if new one is null)
    if (oldName && oldName !== newVal.name) {
      removeWeather(oldName);
    }
    // Add new layer if it's not null
    if (newVal.name && newVal.type) {
      addWeather(newVal.type, newVal.name);
    }
  },

  // ── overlay listener lifecycle ──
  initOverlayListeners({dispatch, state}){
    const applyPersistedStateToMap = () => {
      console.log("Attempting to apply persisted state to map..."); // DEBUG LOG
      // Add overlays based on the *current state* (which was loaded from localStorage)
      // Check map readiness more carefully
      if (!isMapReady(true)) {
        console.log("Map or style not ready yet."); // DEBUG LOG
        return; // Don't proceed if map/style isn't fully ready
      }

      console.log("Map ready, applying overlays:", {
        slope: state.slopeAngleActive,
        officialHikes: state.officialHikesActive,
        cell: state.cellCoverageActive,
        landAccess: state.landAccessActive,
        weather: state.activeWeatherLayer
      }); // DEBUG LOG

      // Apply non-trail overlays
      state.slopeAngleActive   ? addSlope()  : removeSlope(); // Ensure removal if false
      state.officialHikesActive ? addOfficialHikes() : removeOfficialHikes(); // Ensure removal if false
      state.cellCoverageActive ? addCell()   : removeCell();   // Ensure removal if false
      state.landAccessActive   ? addLandAccess() : removeLandAccess();
      state.cyclingRoutesActive ? addCyclingRoutes() : removeCyclingRoutes();
      if (state.heatmapActive) {
        RevenueCatWrapper.isProOrProPlus().catch(() => false).then(isPro => addHeatmap(isPro));
      } else { removeHeatmap(); }

      // Apply weather overlay
      // Remove any potentially lingering weather layer first
      // (This might be redundant if destroyOverlayListeners is always called, but adds safety)
      removeWeather('temperature'); // Example: remove all possible types if unsure
      removeWeather('cloud');
      removeWeather('rainfall');
      removeWeather('snowfall');
      removeWeather('wind');
      // Add the active one
      if (state.activeWeatherLayer.name && state.activeWeatherLayer.type) {
        addWeather(state.activeWeatherLayer.type, state.activeWeatherLayer.name);
      }

      // Restore trail sources - this is where your original issue might be
      dispatch('refreshLineSources');

      // Re-initialize click handler AFTER layers might have been added
      dispatch('initMapClickHandler');
    };

    const onLoaded = () => {
      setTimeout(() => {

        dispatch('initActiveSources');
        // Try to apply persisted state to map
        applyPersistedStateToMap();
      }, 2500)
    };

    const onStyle = () => {
      setTimeout(() => {
        console.log("map-style-loaded event received."); // DEBUG LOG
        // Try to apply persisted state to map
        applyPersistedStateToMap();
      }, 2500)
    };

    // --- Event Listener Setup ---
    // Ensure prior listeners are removed before adding new ones if this action can be called multiple times
    if ((actions as any)._unsubscribeMapHandlers) {
      (actions as any)._unsubscribeMapHandlers();
    }

    // Fired by CustomiseOverlayLineStyleView on every edit, and by the
    // cloud-sync bridge when a value arrives from iOS/Android.
    const onOverlayLineStyles = () => { dispatch('refreshOverlayLineStyles'); };

    emitter.on('map-loaded', onLoaded);
    emitter.on('map-style-loaded', onStyle);
    emitter.on('overlay-line-styles:changed', onOverlayLineStyles);

    // Store the unsub functions
    (actions as any)._unsubscribeMapHandlers = () => {
      emitter.off('map-loaded', onLoaded);
      emitter.off('map-style-loaded', onStyle);
      emitter.off('overlay-line-styles:changed', onOverlayLineStyles);
      // Clear the stored function itself
      delete (actions as any)._unsubscribeMapHandlers;
    };

    // Initial attempt to apply state in case map is already loaded when component mounts
    applyPersistedStateToMap();
  },

  destroyOverlayListeners({ state }){
    // Unsubscribe from map events
    if ((actions as any)._unsubscribeMapHandlers) {
      (actions as any)._unsubscribeMapHandlers();
    }

    // Remove click handler
    if (mapClickHandlerRef && window.map) {
      window.map.off('click', mapClickHandlerRef);
      mapClickHandlerRef = null;
      console.log("Map click handler removed."); // DEBUG LOG
    }

    // Clean up layers from the map (optional but good practice)
    // These are already called if toggling OFF, but ensures cleanup on component destroy
    // removeSlope();
    // removeHiiker();
    // removeCell();
    // if(state.activeWeatherLayer.name) {
    //   removeWeather(state.activeWeatherLayer.name);
    // }
    // Note: Trail source removal happens via toggleLineSource when activeSources changes
    // or potentially needs manual cleanup here if component unmounts unexpectedly.
    console.log("Overlay listeners destroyed."); // DEBUG LOG
  },

  // ────────────────────── ORIGINAL trail-source logic ─────────────────────
  // (No changes needed below here unless debugging reveals issues in toggleLineSource)

  initActiveSources({ state, commit }) {
    const validSources = Object.keys(state.trailOptions);
    // No user check needed if state is already loaded from localStorage?
    // Or keep it if anonymous users shouldn't have trails saved.
    // if (!window.user) { commit('SET_ACTIVE_SOURCES', []); return; }

    const urlParams = new URLSearchParams(window.location.search);
    let useUrl = false; const fromUrl:string[]=[];
    validSources.forEach(src=>{
      if(urlParams.has(src)){ useUrl=true; if(urlParams.get(src)==='true') fromUrl.push(src);}
    });

    if(useUrl){
      console.log("Loading active sources from URL params:", fromUrl); // DEBUG LOG
      commit('SET_ACTIVE_SOURCES', fromUrl); // SET_ACTIVE_SOURCES now persists
    } else {
      // State is already loaded from localStorage, just ensure validity?
      // Or perhaps this action is primarily for URL param override?
      // If state is primary source, this might only need to filter invalid sources.
      const loadedFromStorage = state.activeSources; // Already parsed
      const filtered = loadedFromStorage.filter(s => validSources.includes(s));
      if (filtered.length !== loadedFromStorage.length) {
        console.log("Filtered invalid active sources from localStorage:", filtered); // DEBUG LOG
        // Commit only if filtering actually changed something
        commit('SET_ACTIVE_SOURCES', filtered);
      } else {
        console.log("Active sources loaded from localStorage:", loadedFromStorage); // DEBUG LOG
      }
    }
  },

  forceOneSource({commit,dispatch},src:string){ commit('SET_ACTIVE_SOURCES',[src]); dispatch('refreshLineSources'); },

  toggleTrail({state,commit,dispatch},src:string){
    // Use mutations which now handle persistence
    state.activeSources.includes(src) ? commit('REMOVE_ACTIVE_SOURCE', src) : commit('ADD_ACTIVE_SOURCE', src);
    // Apply the change to the map
    dispatch('toggleLineSource', src);
  },

  /**
   * Show or hide a personal-trail vector source (and its 2–3 layers).
   *
   *  ┌─────────────┐  z: 1  white halo     (borderId)
   *  │  TRAIL      │  z: 0  coloured line  (layerId)
   *  └─────────────┘  z:-1  optional pts   (pointId)   ← if you ever add it
   *
   * The helper is idempotent: calling it twice with the same state does nothing.
   */
  toggleLineSource({ state, getters }, src: string) {
    LineSourceHelper.toggle(
      src,
      state.activeSources.includes(src),
      getters.minZoomForSource(src),
      state.sourceColors[src]
    );
  },

  /**
   * Re-paints every active line overlay from the current customised styles.
   *
   * Restyles in place rather than toggling the sources off and on: the personal
   * trail layers are backed by vector tiles fetched with a cache-busting `t=`
   * param, so a teardown/re-add on every colour tap would re-download the whole
   * overlay mid-gesture.
   */
  refreshOverlayLineStyles({ state }) {
    if (!isMapReady()) return;
    state.activeSources.forEach((src: string) => {
      LineSourceHelper.restyle(src, state.sourceColors[src]);
    });
    if (state.officialHikesActive) restyleOfficialHikes();
  },

  addFeatureFromClick({ commit, dispatch }, { source, properties }) {
    commit('SET_MINI_LOADER', true);

    // Ensure properties exists and has an id or equivalent
    if (!properties) {
      console.warn(`Clicked feature from source ${source} has no properties.`);
      commit('SET_MINI_LOADER', false);
      return;
    }
    // Determine the ID property based on the source
    let id = null;
    switch (source) {
      case 'get_user_recordings':       id = properties.id; break;
      case 'get_user_planned_trails':   id = properties.hike_id; break;
      case 'get_user_completed_hikes':  id = properties.hike_id; break;
      case 'get_published_trails':      id = properties.trail_id; break;
      case HIIKER_TRAILS_SOURCE_ID:     id = properties.id; break; // Trail ID from vector tiles
      default:
        console.warn(`Unhandled click source: ${source}`);
        commit('SET_MINI_LOADER', false);
        return;
    }

    if (id === null || id === undefined) {
      console.warn(`Could not determine ID for clicked feature from source ${source}:`, properties);
      commit('SET_MINI_LOADER', false);
      return;
    }

    console.log(`Feature clicked - Source: ${source}, ID: ${id}`); // DEBUG LOG

    // Dispatch based on source
    switch (source) {
      case 'get_user_recordings':       dispatch('getRecording', id); break;
      case 'get_user_planned_trails':   dispatch('getHike', id); break;
      case 'get_user_completed_hikes':  dispatch('getHike', id); break;
      case 'get_published_trails':      dispatch('getTrail', id); break;
      case HIIKER_TRAILS_SOURCE_ID:     dispatch('getOfficialTrail', id); break; // Try hike first, fallback to trail
    }
  },


  getTrail({dispatch,commit},id:string){
    console.log("Fetching trail with ID:", id); // DEBUG LOG
    graph_client.request(GetTrail,{id}).then((d)=>{
      // @ts-ignore
      if(d?.trail?.multilinestring){
        // @ts-ignore
        dispatch('addLineLayToMap',JSON.parse(d.trail.multilinestring));
        // @ts-ignore
        window.trailCardDataTrail?.(d.trail); // Use optional chaining
      } else { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_parsing_trail')); console.error("Error parsing trail data:", d); }
    }).catch((err)=> { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_fetching_trail')); console.error("Error fetching trail:", err); })
      .finally(()=>commit('SET_MINI_LOADER',false));
  },

  getHike({dispatch,commit},id:string){
    console.log("Fetching hike with ID:", id);
    graph_client.request(GetHikeForTrailCard,{id}).then((d)=>{
      // @ts-ignore
      if(d?.hike?.geojsonMultilinestringsWithStartEndPointsString){
        // @ts-ignore
        const geoJson = JSON.parse(d.hike.geojsonMultilinestringsWithStartEndPointsString);
        // @ts-ignore
        dispatch('addLineLayToMap', geoJson);
        // @ts-ignore
        window.trailCardDataHike?.(d.hike); // Use optional chaining
      } else { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_parsing_hike')); console.error("Error parsing hike data:", d); }
    }).catch((err)=> { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_fetching_hike')); console.error("Error fetching hike:", err); })
      .finally(()=>commit('SET_MINI_LOADER',false));
  },

  getPtv2PlannedTrail({dispatch,commit},id:string){
    console.log("Fetching PTV2 planned trail with ID:", id); // DEBUG LOG
    graph_client.request(GetPtv2PlannedTrail,{id}).then(d=>{
      // @ts-ignore
      if(d?.ptv2PlannedTrailById?.geojsonFeatureCollection){
        // @ts-ignore
        dispatch('addLineLayToMap',d.ptv2PlannedTrailById.geojsonFeatureCollection);
        // @ts-ignore
        window.trailCardDataPtv2PlannedTrail?.(d.ptv2PlannedTrailById); // Use optional chaining
      } else { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_parsing_planned_trail')); console.error("Error parsing PTV2 planned trail data:", d); }
    }).catch((err)=> { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_fetching_planned_trail')); console.error("Error fetching PTV2 planned trail:", err); })
      .finally(()=>commit('SET_MINI_LOADER',false));
  },

  getRecording({dispatch,commit},id:string){
    console.log("Fetching recording with ID:", id); // DEBUG LOG
    // Ensure ID is string if GraphQL expects it
    graph_client.request(GetRecordedTrailByRecordingId,{id:`${id}`}).then(d=>{
      // @ts-ignore
      if(d?.recording?.lineString){
        // @ts-ignore
        dispatch('addLineLayToMap',JSON.parse(d.recording.lineString));
        // @ts-ignore
        window.trailCardDataRecording?.(d.recording); // Use optional chaining
      } else { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_parsing_recording')); console.error("Error parsing recording data:", d); }
    }).catch((err)=> { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_fetching_recording')); console.error("Error fetching recording:", err); })
      .finally(()=>commit('SET_MINI_LOADER',false));
  },

  // Fetch official trail - tries to get hike first (if trail was migrated), then falls back to trail
  getOfficialTrail({dispatch,commit},trailId:string){
    console.log("Fetching official trail with ID:", trailId);
    // Try to fetch hike by legacy trail ID
    graph_client.request(GetHikeByLegacy,{legacyId:trailId, legacyType:'Trail'}).then((d)=>{
      // @ts-ignore
      if(d?.hikeByLegacy){
        // Found a hike - display it
        console.log("Found hike for trail ID:", trailId);
        // @ts-ignore
        if(d.hikeByLegacy.geojsonMultilinestringsWithStartEndPointsString){
          // @ts-ignore
          const geoJson = JSON.parse(d.hikeByLegacy.geojsonMultilinestringsWithStartEndPointsString);
          // @ts-ignore
          dispatch('addLineLayToMap', geoJson);
          // @ts-ignore
          window.trailCardDataHike?.(d.hikeByLegacy);
        } else { alert(i18n.global.t('base_map_dialog.base_map_data_overlays_module.error_parsing_hike')); console.error("Error parsing hike data:", d); }
        commit('SET_MINI_LOADER',false);
      } else {
        // No hike found - fetch trail instead
        console.log("No hike found, fetching trail:", trailId);
        dispatch('getTrail', trailId);
      }
    }).catch((err)=> {
      // Error or no hike - fetch trail instead
      console.log("Error fetching hike, falling back to trail:", err);
      dispatch('getTrail', trailId);
    });
  },

  removeLineLayer(){
    if(!window.map||!window.map.isStyleLoaded())return;
    console.log("Removing individual line layer"); // DEBUG LOG
    // Check existence before removing to prevent errors
    if (window.map.getLayer('individual-line-border')) window.map.removeLayer('individual-line-border');
    if (window.map.getLayer('individual-line')) window.map.removeLayer('individual-line');
    if (window.map.getSource('individual-line')) window.map.removeSource('individual-line');
  },

  showCyclingPopup(_ctx: any, { lngLat, properties }: { lngLat: mapboxgl.LngLat; properties: any }) {
    if (!window.map) return;

    // Inject popup styles once, matching the LandAccess popup design pattern
    const STYLE_ID = 'hiiker-cycling-popup-styles';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
        .hiiker-cycling-popup .mapboxgl-popup-content {
          background: rgba(23,24,29,0.95); backdrop-filter: blur(8px);
          border-radius: 10px; padding: 0; color: #fff;
          box-shadow: 0 4px 20px rgba(0,0,0,0.35);
        }
        .hiiker-cycling-popup .mapboxgl-popup-tip { border-top-color: rgba(23,24,29,0.95); }
        .hiiker-cycling-popup .mapboxgl-popup-close-button { color:#fff; font-size:18px; padding:4px 8px; opacity:0.7; background:transparent; border:none; }
        .hiiker-cycling-popup .mapboxgl-popup-close-button:hover { opacity:1; background:transparent; }
        .cr-popup { padding:14px 16px 12px; font-size:13px; line-height:1.45; }
        .cr-header { display:flex; align-items:center; gap:10px; margin-bottom:12px; padding-bottom:10px; border-bottom:1px solid rgba(255,255,255,0.15); }
        .cr-title { font-size:15px; font-weight:700; }
        .cr-row { display:flex; justify-content:space-between; align-items:baseline; gap:16px; padding:5px 0; border-bottom:1px solid rgba(255,255,255,0.08); font-size:12px; }
        .cr-row:last-child { border-bottom:none; padding-bottom:0; }
        .cr-label { color:rgba(255,255,255,0.55); white-space:nowrap; flex-shrink:0; }
        .cr-value { font-weight:600; text-align:right; }
      `;
      document.head.appendChild(style);
    }

    const p        = properties || {};
    const color    = p.Desc_ === 'TrafficFree' ? '#FFA500' : p.Desc_ === 'OnRoad' ? '#000080' : '#888';
    const isDashed = p.RouteType === 'LINK';
    // Insert a space before each capital letter that follows a lowercase letter
    // e.g. TrafficFree → Traffic Free, OnRoad → On Road, UnsealedFirm → Unsealed Firm
    const titleize = (v: any): string => {
      if (v === null || v === undefined || v === '') return '—';
      return String(v).replace(/([a-z])([A-Z])/g, '$1 $2');
    };
    const row = (label: string, value: any) =>
      `<div class="cr-row"><span class="cr-label">${label}</span><span class="cr-value">${titleize(value)}</span></div>`;

    const html = `
      <div class="cr-popup">
        <div class="cr-header">
          <svg width="32" height="10" viewBox="0 0 32 10" style="flex-shrink:0">
            ${isDashed
              ? `<line x1="0" y1="5" x2="32" y2="5" stroke="${color}" stroke-width="3" stroke-dasharray="6,4"/>`
              : `<line x1="0" y1="5" x2="32" y2="5" stroke="${color}" stroke-width="3"/>`}
          </svg>
          <span class="cr-title">${i18n.global.t('base_map_dialog.base_map_data_overlays_module.cycling_route')}</span>
        </div>
        ${row(i18n.global.t('base_map_dialog.base_map_data_overlays_module.desc'),     p.Desc_)}
        ${row(i18n.global.t('base_map_dialog.base_map_data_overlays_module.route_no'), p.RouteNo)}
        ${row(i18n.global.t('base_map_dialog.base_map_data_overlays_module.type'),     p.RouteType)}
        ${row(i18n.global.t('base_map_dialog.base_map_data_overlays_module.surface'),  p.Surface)}
        ${row(i18n.global.t('base_map_dialog.base_map_data_overlays_module.lighting'), p.Lighting)}
        ${row(i18n.global.t('base_map_dialog.base_map_data_overlays_module.quality'),  p.Quality)}
        ${row(i18n.global.t('base_map_dialog.base_map_data_overlays_module.greenway'), p.Greenway)}
      </div>`;

    if ((window as any)._cyclingPopup) {
      (window as any)._cyclingPopup.remove();
    }

    (window as any)._cyclingPopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: '300px',
      className: 'hiiker-cycling-popup'
    })
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(window.map);
  },

  addLineLayToMap({dispatch},fc:any){
    if(!window.map||!window.map.isStyleLoaded())return;
    console.log("Adding individual line layer to map"); // DEBUG LOG
    dispatch('removeLineLayer'); // Ensure previous one is gone
    const color='#ff00f2',dark=darkenColor(color);
    try {
      window.map.addSource('individual-line',{type:'geojson',data:fc});
      window.map.addLayer({id:'individual-line-border',type:'line',source:'individual-line',
        layout:{'line-join':'round','line-cap':'round'},
        paint:{'line-color':dark,'line-width':['interpolate',['linear'],['zoom'],0,4,14,10]}});
      window.map.addLayer({id:'individual-line',type:'line',source:'individual-line',
        layout:{'line-join':'round','line-cap':'round'},
        paint:{'line-color':color,'line-width':['interpolate',['linear'],['zoom'],0,2,14,8]}});
    } catch (error) {
      console.error("Error adding individual line layer:", error); // DEBUG LOG
      // Clean up source if layer adding failed
      if (window.map.getSource('individual-line')) window.map.removeSource('individual-line');
    }
  },

  refreshLineSources({state, dispatch}){
    console.log("Refreshing line sources:", state.activeSources); // DEBUG LOG
    state.activeSources.forEach(s => {
      if (!window.map.getSource(s)) {
        console.log(`Source ${s} needs to be added during refresh.`); // DEBUG LOG
        dispatch('toggleLineSource', s);
      } else {
        // Optional: Ensure layers are visible if source exists
        const layerId = s;
        const borderId = `${s}-border`;
        if (window.map.getLayer(layerId)) window.map.setLayoutProperty(layerId, 'visibility', 'visible');
        if (window.map.getLayer(borderId)) window.map.setLayoutProperty(borderId, 'visibility', 'visible');
      }
    });
  },


  initMapClickHandler({ state, dispatch, rootState }) {
    if (!window.map) { console.error('Map missing for initMapClickHandler'); return; }

    // Remove the previous handler to prevent duplicates
    if (mapClickHandlerRef) {
      window.map.off('click', mapClickHandlerRef);
      console.log("Removed existing map click handler."); // DEBUG LOG
      mapClickHandlerRef = null;
    }

    const HIT_RADIUS = 6;

    // Define the handler function
    mapClickHandlerRef = (e: any) => {
      // Prevent clicks during planning mode
      window.removeTrailLine = null; // Reset the function reference

      // *** GENERATE layersToQuery INSIDE the handler ***
      const layersToQuery: string[] = [];
      // Read FRESH state here
      state.activeSources.forEach(src => {
        layersToQuery.push(src); // Assumes layer ID matches source ID
        // layersToQuery.push(`${src}-border`); // Optional: if borders are clickable
      });
      if (state.officialHikesActive) { // Read FRESH state here
        layersToQuery.push(HIIKER_TRAILS_MAIN_ID); // Adjust if your layer name is different
        // layersToQuery.push('official-hikes-border-layer');
      }
      if (state.cyclingRoutesActive) {
        layersToQuery.push(CYCLING_NCN_ID);
        layersToQuery.push(CYCLING_LINK_ID);
      }
      // Add any other logic for dynamically determining clickable layers based on current state
      state.miniLoader = false
      if (layersToQuery.length === 0) {
        console.log("Click occurred, but no layers are active/queryable based on current state."); // DEBUG LOG
        window.trailCardClearData?.();
        dispatch('removeLineLayer');
        return;
      }
      // --- Optional but Recommended: Filter against actual map layers ---
      // This defends against brief timing issues where state says a layer is active,
      // but it hasn't quite been added to the map yet, or was just removed.
      const currentlyQueryableLayers = layersToQuery.filter(layerId => window.map?.getLayer(layerId));

      if (currentlyQueryableLayers.length === 0) {
        console.log("Click occurred, state indicates layers should be queryable, but none currently exist on map."); // DEBUG LOG
        window.trailCardClearData?.();
        dispatch('removeLineLayer');
        return;
      }
      // --- End Optional Filter ---


      console.log("Map click: Querying layers based on current state:", currentlyQueryableLayers); // Use filtered list

      const bbox: [[number, number], [number, number]] = [
        [e.point.x - HIT_RADIUS, e.point.y - HIT_RADIUS],
        [e.point.x + HIT_RADIUS, e.point.y + HIT_RADIUS]
      ];

      // Use the dynamically generated (and potentially filtered) list
      const feats = window.map!.queryRenderedFeatures(bbox, { layers: currentlyQueryableLayers });

      if (feats.length === 0) {
        console.log("Map click: No features found in query area."); // DEBUG LOG
        window.trailCardClearData?.();
        dispatch('removeLineLayer'); // Clear any previously selected individual line
        return;
      }

      const f = feats[0];
      console.log("Map click: Feature found:", f); // DEBUG LOG

      // Cycling routes: show an inline popup from vector tile properties (no server fetch)
      // @ts-ignore
      if (f.layer.id === CYCLING_NCN_ID || f.layer.id === CYCLING_LINK_ID) {
        window.trailCardClearData?.();   // clear any open hiking trail card
        dispatch('removeLineLayer');      // clear any individually-drawn trail line
        dispatch('showCyclingPopup', { lngLat: e.lngLat, properties: f.properties });
        return;
      }

      let logicalSource = f.source;
      // @ts-ignore
      if (f.layer.id === HIIKER_TRAILS_MAIN_ID || f.layer.id === HIIKER_TRAILS_BORDER_ID) {
        logicalSource = HIIKER_TRAILS_SOURCE_ID;
      }
      // Add similar mappings if other layer IDs don't match logical source IDs

      if (f.properties && logicalSource) {
        dispatch('addFeatureFromClick', { source: logicalSource, properties: f.properties });
        window.removeTrailLine = () => {
          dispatch('removeLineLayer'); // Clear any previously selected individual line
          window.removeTrailLine = null; // Reset the function reference
        };
      } else {
        console.warn("Clicked feature missing properties or logical source could not be determined:", f); // DEBUG LOG
      }
    };

    // Attach the handler (which now calculates layers internally)
    window.map.on('click', mapClickHandlerRef);
  },
};

// ─────────────────────────── Getters ────────────────────────────
const getters: GetterTree<BaseMapDataOverlaysState,RootState> = {
  // Checks the persisted state
  isSourceActive:(s)=>(src:string)=>s.activeSources.includes(src),
  // No changes needed here
  minZoomForSource:()=>src=>src==='get_published_trails'?10:5,
};

// ─────────────────────────── Export module ──────────────────────
const mapDataOverlaysModule: Module<BaseMapDataOverlaysState,RootState> = {
  namespaced:true, state, mutations, actions, getters,
};
export default mapDataOverlaysModule;
